import React, { ReactNode, useEffect, useState } from 'react';
import '@fontsource-variable/archivo/wdth.css';
import '@fontsource-variable/jetbrains-mono';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TierBadge from './TierBadge';
import TrialBanner from './TrialBanner';
import {
  HomeIcon,
  ChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  BriefcaseIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import config from '../../config';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Trader theme (Oct 2026): tokens, fonts and radius for the signed-in app,
  // set on <body> so portaled modals and drawers pick it up too. Marketing
  // pages don't render this layout and keep their own look.
  useEffect(() => {
    document.body.classList.add('theme-trader');
    return () => document.body.classList.remove('theme-trader');
  }, []);

  // Beta: Force beginner mode for all users
  const isBeginnerMode = true;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const allNavigation = [
    { name: 'Dive In', href: '/dive-in', icon: HomeIcon },
    { name: 'Stock Screening', href: '/screening', icon: MagnifyingGlassIcon },
    { name: 'Stock Analysis', href: '/analysis', icon: ChartBarIcon },
    { name: 'Portfolio Tracking', href: '/portfolio/positions', icon: BriefcaseIcon },
    // Leaderboard hidden from the menu until frameworks have tagged trades (route still works).
    { name: 'AI Coach', href: '/ai-coach', icon: SparklesIcon },
  ];

  // Filter out Stock Analysis link for beginner users
  const navigation = isBeginnerMode
    ? allNavigation.filter(item => item.name !== 'Stock Analysis')
    : allNavigation;

  // Bottom tab bar items for mobile (thumb-friendly core navigation)
  const bottomTabs = [
    { name: 'Home', href: '/dive-in', icon: HomeIcon },
    { name: 'Screener', href: '/screening', icon: MagnifyingGlassIcon },
    { name: 'Portfolio', href: '/portfolio/positions', icon: BriefcaseIcon },
    { name: 'Settings', href: '/settings', icon: CogIcon },
  ];

  // Check if a tab is active (match prefix for portfolio sub-routes)
  const isTabActive = (href: string) => {
    if (href === '/portfolio/positions') {
      return location.pathname.startsWith('/portfolio');
    }
    return location.pathname === href;
  };

  return (
    <div className="app-layout">
      {/* Mobile sidebar overlay — desktop only now, kept for edge cases */}
      {sidebarOpen && (
        <div className="app-mobile-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Professional trading interface sidebar — desktop only */}
      <div className={`app-sidebar ${sidebarOpen ? 'app-sidebar-open' : 'app-sidebar-closed'}`}>
        <div className="app-sidebar-header">
          <Link to="/dive-in" className="app-brand-sidebar">
            <span className="app-logo-sidebar" aria-hidden="true">B</span>
            <span className="app-brand-text">babylon<span>.markets</span></span>
            <span className="app-beta">Beta</span>
          </Link>
          <button
            className="app-mobile-close"
            onClick={() => setSidebarOpen(false)}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <nav className="app-sidebar-nav">
          <div className="app-nav-section">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = isTabActive(item.href);

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`app-nav-item ${isActive ? 'app-nav-item-active' : ''}`}
                >
                  <Icon className="app-nav-icon" />
                  <span className="app-nav-text">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Dev-only experimental nav — visible only when REACT_APP_ENVIRONMENT=development */}
        {config.ENVIRONMENT === 'development' && (
          <div
            className="app-nav-section"
            style={{
              marginTop: 'auto',
              paddingTop: 12,
              borderTop: '1px solid #232a38',
            }}
          >
            <div
              style={{
                padding: '4px 16px 6px',
                fontSize: 10,
                textTransform: 'uppercase',
                color: '#6b7280',
                letterSpacing: 0.5,
              }}
            >
              Dev tools
            </div>
            <Link
              to="/dev/combo-analytics"
              onClick={() => setSidebarOpen(false)}
              className={`app-nav-item ${
                location.pathname === '/dev/combo-analytics' ? 'app-nav-item-active' : ''
              }`}
            >
              <BeakerIcon className="app-nav-icon" />
              <span className="app-nav-text">Combo Analytics</span>
            </Link>
          </div>
        )}

        {/* User section at bottom */}
        <div className="app-sidebar-footer">
          <div className="app-user-info">
            <div className="app-user-avatar">
              <UserIcon className="app-avatar-icon" />
            </div>
            <div className="app-user-details">
              <p className="app-user-name">
                {user?.full_name || user?.username || user?.email}
              </p>
              <div className="app-user-role" style={{ marginTop: 3 }}>
                <TierBadge showTrialDays />
              </div>
            </div>
            <Link to="/settings" className="app-settings-btn" onClick={() => setSidebarOpen(false)} title="Settings">
              <CogIcon className="app-settings-icon" />
            </Link>
          </div>
          <button
            onClick={handleLogout}
            className="app-logout-btn"
          >
            <ArrowRightOnRectangleIcon className="app-logout-icon" />
            <span className="app-logout-text">Logout</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="app-main-content">
        {/* Fixed top header for mobile — logo only, no hamburger */}
        <div className="app-mobile-header">
          <Link to="/dive-in" className="app-mobile-title app-brand-sidebar">
            <span className="app-logo-sidebar" aria-hidden="true">B</span>
            <span className="app-brand-text">babylon<span>.markets</span></span>
            <span className="app-beta">Beta</span>
          </Link>
        </div>

        {/* Page content — scrolls between fixed header and fixed tab bar */}
        <main className="app-page-content">
          <TrialBanner />
          {children}
        </main>
      </div>

      {/* Bottom tab bar — mobile only */}
      <nav className="app-bottom-tab-bar">
        {bottomTabs.map((tab) => {
          const Icon = tab.icon;
          const active = isTabActive(tab.href);
          return (
            <Link
              key={tab.name}
              to={tab.href}
              className={`app-bottom-tab ${active ? 'app-bottom-tab-active' : ''}`}
            >
              <Icon className="app-bottom-tab-icon" />
              <span className="app-bottom-tab-label">{tab.name}</span>
            </Link>
          );
        })}
      </nav>

    </div>
  );
};

export default Layout;